import type { InvoiceRegistryRow } from '@crm/shared'

/**
 * Разбор реестра счетов из журнала документов 1С (этап 3, Б4) — таблица Excel уже
 * прочитана в строки ячеек. Колонки ищем по заголовкам: «Номер», «Дата», «Код»,
 * «Покупатель», «Сумма», «Состояние»; порядок и лишние колонки («Номер картинки …»)
 * не важны. Образец — реестр за сентябрь 2026 (docs/stage-3-plan.md, «Б4»).
 */

type Cell = string | number | Date | boolean | null | undefined

const text = (v: Cell): string => (v == null ? '' : String(v).trim())

/** «01.09.2026», дата Excel числом или объектом → `2026-09-01`. */
export function registryDate(v: Cell): string | null {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10)
  if (typeof v === 'number' && Number.isFinite(v)) {
    // Число Excel — дни от 30.12.1899.
    const d = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86_400_000)
    return d.toISOString().slice(0, 10)
  }
  const m = text(v).match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/)
  if (!m) return null
  return `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`
}

/** «4 292,95» или число → число; пусто или мусор → null. */
export function registrySum(v: Cell): number | null {
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? Math.round(v * 100) / 100 : null
  const cleaned = text(v)
    .replace(/[\s\xA0]/g, '')
    .replace(',', '.')
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100) / 100
}

export class InvoiceRegistryFormatError extends Error {}

export function parseInvoiceRegistry(rows: Cell[][]): InvoiceRegistryRow[] {
  const headIdx = rows.findIndex((r) => {
    const cells = (r ?? []).map(text)
    return cells.includes('Номер') && cells.includes('Сумма')
  })
  const head = headIdx >= 0 ? (rows[headIdx] ?? []).map(text) : []
  const col = (name: string) => head.indexOf(name)
  const iNumber = col('Номер')
  const iCode = col('Код')
  if (headIdx < 0 || iCode < 0) {
    throw new InvoiceRegistryFormatError(
      'Это не реестр счетов из 1С: в таблице должны быть колонки «Номер», «Код», «Покупатель» и «Сумма»',
    )
  }
  const out: InvoiceRegistryRow[] = []
  for (const r of rows.slice(headIdx + 1)) {
    const number = text(r?.[iNumber])
    if (!number) continue
    out.push({
      number: number.slice(0, 40),
      date: registryDate(r?.[col('Дата')]),
      clientCode: text(r?.[iCode]).slice(0, 64),
      buyer: text(r?.[col('Покупатель')]).slice(0, 300),
      sum: registrySum(r?.[col('Сумма')]),
      state: text(r?.[col('Состояние')]).slice(0, 100) || null,
    })
  }
  if (out.length === 0) {
    throw new InvoiceRegistryFormatError('В реестре нет ни одного счёта с номером')
  }
  return out
}
